/**
 * Upload a large file straight to Azure Blob Storage with a SAS (`BE-17`).
 *
 * Written by hand rather than pulled from `@azure/storage-blob` because the
 * browser only needs two of that library's REST calls, and the library is
 * hundreds of kilobytes the CMS bundle would carry for a dialog most operators
 * never open. What is here is Put Block and Put Block List, and nothing else.
 *
 * ## Why blocks at all
 *
 * A single PUT of a master file is one HTTP request that either survives for
 * however long gigabytes take on a hotel's uplink, or fails and starts again
 * from zero. Blocks turn that into many small requests, each individually
 * retryable, and make honest progress reporting possible.
 *
 * ⚠️ **The storage account must allow the CMS origin in its CORS rules** —
 * `PUT` with `x-ms-blob-type` and `x-ms-blob-content-type` on the allowed
 * headers. Without it the browser refuses the request before it is sent, and
 * the failure looks like a network error rather than a configuration one.
 */

/** 8 MB — the same block size the portal's single-title path uses. */
const BLOCK_SIZE = 8 * 1024 * 1024;

/** How many blocks are in flight at once. */
const CONCURRENCY = 4;

/** Attempts per block before the whole upload gives up. */
const BLOCK_ATTEMPTS = 3;

/**
 * Block ids must all be the SAME LENGTH once base64-encoded, or the service
 * rejects the block list. Zero-padding the index is what guarantees that; the
 * commonest bug here is an id built from a number whose width changes at 10.
 */
function blockId(index: number): string {
  return btoa(String(index).padStart(8, "0"));
}

function appendQuery(url: string, extra: string): string {
  return url.includes("?") ? `${url}&${extra}` : `${url}?${extra}`;
}

async function putBlock(
  uploadUrl: string,
  id: string,
  body: Blob,
  signal?: AbortSignal
): Promise<void> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= BLOCK_ATTEMPTS; attempt++) {
    let res: Response | null = null;
    try {
      res = await fetch(
        appendQuery(uploadUrl, `comp=block&blockid=${encodeURIComponent(id)}`),
        { method: "PUT", body, signal }
      );
    } catch (e) {
      // Only a TRANSPORT failure lands here. The status checks below are
      // deliberately outside this try: throwing a refusal inside it would be
      // caught by this very catch and retried, which is the opposite of what
      // "do not retry a 4xx" means - and it took a test to notice.
      if ((e as Error).name === "AbortError") throw e;
      lastError = e;
    }

    if (res) {
      if (res.ok) return;
      // 4xx is a bad request or an expired SAS. Retrying cannot help; it only
      // delays the real message by a second and a half.
      if (res.status < 500) {
        throw new Error(`Storage refused a block (${res.status}).`);
      }
      lastError = new Error(`Storage answered ${res.status}.`);
    }
    await new Promise((r) => setTimeout(r, attempt * 500));
  }
  throw lastError instanceof Error
    ? lastError
    : new Error("Block upload failed");
}

export interface BlockUploadOptions {
  onProgress?: (uploadedBytes: number, totalBytes: number) => void;
  signal?: AbortSignal;
  contentType?: string;
}

/**
 * PUT a file in blocks, then commit the list.
 *
 * Progress is reported in BYTES COMMITTED to the wire, not blocks started, so a
 * bar that says 90% means nine tenths of the file is in storage.
 */
export async function uploadInBlocks(
  uploadUrl: string,
  file: Blob,
  options: BlockUploadOptions = {}
): Promise<void> {
  const total = file.size;
  const count = Math.max(1, Math.ceil(total / BLOCK_SIZE));
  const ids = Array.from({ length: count }, (_, i) => blockId(i));

  let done = 0;
  let next = 0;

  async function worker(): Promise<void> {
    for (;;) {
      const index = next++;
      if (index >= count) return;
      const start = index * BLOCK_SIZE;
      const chunk = file.slice(start, Math.min(start + BLOCK_SIZE, total));
      await putBlock(uploadUrl, ids[index], chunk, options.signal);
      done += chunk.size;
      options.onProgress?.(done, total);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, count) }, () => worker())
  );

  // Committing is what makes the blob exist. Until this succeeds the blocks are
  // uncommitted and the server's own "did the bytes land?" check will correctly
  // say no - which is exactly the truncated-upload case we refuse to dispatch.
  const list = `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids
    .map((id) => `<Latest>${id}</Latest>`)
    .join("")}</BlockList>`;

  const res = await fetch(appendQuery(uploadUrl, "comp=blocklist"), {
    method: "PUT",
    headers: {
      "Content-Type": "application/xml",
      "x-ms-blob-content-type": options.contentType || "video/mp4",
    },
    body: list,
    signal: options.signal,
  });
  if (!res.ok) {
    throw new Error(
      `Storage would not commit the upload (${res.status}). The file is not saved.`
    );
  }
}
